import {
  AdminAccessPermissionLevel,
  DealActivityPermissionLevel,
  DealAssistantPermissionLevel,
  DealRolePermissions,
  DealRolePermissionsInput,
  DealRoleType,
  DealSettingsPermissionLevel,
  DocumentsPermissionLevel,
  GuestAccessPermissionLevel,
  QuestionsPermissionLevel,
} from "@/src/graphql/generated";
import {
  BotIcon,
  CircleQuestionMarkIcon,
  FolderIcon,
  ListIcon,
  LockIcon,
  SettingsIcon,
} from "lucide-react";
import { ReactNode } from "react";

export type PermissionToggleOption = {
  value: string;
  label: string;
};

export type PermissionSection = {
  key: keyof DealRolePermissionsInput;
  label: string;
  shortLabel: string;
  icon: ReactNode;
  options: PermissionToggleOption[];
};

const documentsSection: PermissionSection = {
  key: "documents",
  label: "Data Room",
  shortLabel: "Data Room",
  icon: <FolderIcon className="w-4 h-4" />,
  options: [
    { value: DocumentsPermissionLevel.None, label: "No Access" },
    { value: DocumentsPermissionLevel.View, label: "View only" },
    { value: DocumentsPermissionLevel.Download, label: "Download" },
    { value: DocumentsPermissionLevel.Upload, label: "Upload" },
    { value: DocumentsPermissionLevel.Full, label: "Upload, edit and delete" },
  ],
};

const questionsSection: PermissionSection = {
  key: "questions",
  label: "Questions",
  shortLabel: "Questions",
  icon: <CircleQuestionMarkIcon className="w-4 h-4" />,
  options: [
    { value: QuestionsPermissionLevel.None, label: "No Access" },
    { value: QuestionsPermissionLevel.View, label: "View" },
    { value: QuestionsPermissionLevel.Create, label: "Create" },
    { value: QuestionsPermissionLevel.Answer, label: "Answer" },
    { value: QuestionsPermissionLevel.Full, label: "Full" },
  ],
};

const adminAccessSection: PermissionSection = {
  key: "adminAccess",
  label: "Access",
  shortLabel: "Access",
  icon: <LockIcon className="w-4 h-4" />,
  options: [
    { value: AdminAccessPermissionLevel.None, label: "No Access" },
    { value: AdminAccessPermissionLevel.View, label: "View" },
    { value: AdminAccessPermissionLevel.Full, label: "Full" },
  ],
};

const guestAccessSection: PermissionSection = {
  key: "guestAccess",
  label: "Access",
  shortLabel: "Access",
  icon: <LockIcon className="w-4 h-4" />,
  options: [
    { value: GuestAccessPermissionLevel.None, label: "No Access" },
    { value: GuestAccessPermissionLevel.View, label: "View" },
    { value: GuestAccessPermissionLevel.Full, label: "Full" },
  ],
};

const assistantSection: PermissionSection = {
  key: "dealAssistant",
  label: "Assistant",
  shortLabel: "Assistant",
  icon: <BotIcon className="w-4 h-4" />,
  options: [
    { value: DealAssistantPermissionLevel.None, label: "No Access" },
    { value: DealAssistantPermissionLevel.Enabled, label: "Enabled" },
  ],
};

const activitySection: PermissionSection = {
  key: "dealActivity",
  label: "Activity",
  shortLabel: "Activity",
  icon: <ListIcon className="w-4 h-4" />,
  options: [
    { value: DealActivityPermissionLevel.None, label: "No Access" },
    { value: DealActivityPermissionLevel.Enabled, label: "Enabled" },
  ],
};

const settingsSection: PermissionSection = {
  key: "dealSettings",
  label: "Settings",
  shortLabel: "Settings",
  icon: <SettingsIcon className="w-4 h-4" />,
  options: [
    { value: DealSettingsPermissionLevel.None, label: "No Access" },
    { value: DealSettingsPermissionLevel.Enabled, label: "Enabled" },
  ],
};

// The Access row is contextual: internal accounts manage admin access, guest
 // accounts manage guest access. Settings is internal-only — guests never get
 // access to deal settings.
export function getPermissionSections(
  roleType: DealRoleType,
): PermissionSection[] {
  const accessSection =
    roleType === DealRoleType.Guest ? guestAccessSection : adminAccessSection;

  const sections = [
    documentsSection,
    questionsSection,
    accessSection,
    assistantSection,
    activitySection,
  ];

  if (roleType !== DealRoleType.Guest) {
    sections.push(settingsSection);
  }

  return sections;
}

export function permissionsToInput(
  permissions: DealRolePermissions,
): DealRolePermissionsInput {
  return {
    documents: permissions.documents,
    questions: permissions.questions,
    adminAccess: permissions.adminAccess,
    guestAccess: permissions.guestAccess,
    dealAssistant: permissions.dealAssistant,
    dealActivity: permissions.dealActivity,
    dealSettings: permissions.dealSettings,
  };
}

export const DEFAULT_GUEST_PERMISSIONS: DealRolePermissionsInput = {
  documents: DocumentsPermissionLevel.View,
  questions: QuestionsPermissionLevel.View,
  adminAccess: AdminAccessPermissionLevel.None,
  guestAccess: GuestAccessPermissionLevel.None,
  dealAssistant: DealAssistantPermissionLevel.None,
  dealActivity: DealActivityPermissionLevel.None,
  dealSettings: DealSettingsPermissionLevel.None,
};

export const DEFAULT_INTERNAL_PERMISSIONS: DealRolePermissionsInput = {
  documents: DocumentsPermissionLevel.Full,
  questions: QuestionsPermissionLevel.Full,
  adminAccess: AdminAccessPermissionLevel.Full,
  guestAccess: GuestAccessPermissionLevel.None,
  dealAssistant: DealAssistantPermissionLevel.Enabled,
  dealActivity: DealActivityPermissionLevel.Enabled,
  dealSettings: DealSettingsPermissionLevel.Enabled,
};
